import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  listNoteActivities,
  createNote,
  getNoteDetail,
  updateNote,
  deleteNote,
} from '../../../../../../../../services/activity';

const NotesTabContext = createContext(null);

const DEFAULT_PAGE_SIZE = 10;

/**
 * LocalStorage helpers for persisting pinned notes
 */
const getPinStorageKey = (contactId) => `pinned_notes_${contactId}`;

const getPinnedIdsFromStorage = (contactId) => {
  try {
    const stored = localStorage.getItem(getPinStorageKey(contactId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const savePinnedIdsToStorage = (contactId, pinnedIds) => {
  try {
    localStorage.setItem(getPinStorageKey(contactId), JSON.stringify(pinnedIds));
  } catch (err) {
    console.error('Error saving pinned notes to localStorage:', err);
  }
};

const initialFilters = {
  search: '',
  category: 'all',
};

const initialMeta = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  has_next: false,
  has_previous: false,
};

/**
 * Strip HTML tags from content
 */
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
};

/**
 * Map note data to normalized format
 */
let noteIdCounter = 0;
const mapNoteItem = (rawNote) => {
  const extractedNoteUuid = rawNote.note_uuid || rawNote.noteUuid || rawNote.id;
  const fallbackId = `note-${Date.now()}-${++noteIdCounter}`;
  const noteId = extractedNoteUuid || fallbackId;

  const rawTitle = rawNote.note_title || rawNote.noteTitle || rawNote.title || '';
  const rawDescription = rawNote.note_content || rawNote.description || rawNote.content || '';

  return {
    id: noteId,
    noteUuid: extractedNoteUuid || null,
    title: stripHtmlTags(rawTitle),
    description: stripHtmlTags(rawDescription),
    category: rawNote.category || rawNote.categories?.[0] || 'GENERAL',
    owner: {
      name: rawNote.owner_full_name || rawNote.ownerFullName || rawNote.owner?.name || 'Unknown',
      avatar: rawNote.owner_avatar || rawNote.ownerAvatar || rawNote.owner?.avatar || null,
    },
    createdAt: rawNote.created_at || rawNote.createdAt,
    lastUpdated: rawNote.last_update || rawNote.lastUpdate,
    isPinned: rawNote.is_pinned || rawNote.isPinned || false,
    // New fields for updated card design
    status: rawNote.status || rawNote.note_status || null,
    visibility: rawNote.visibility || rawNote.note_visibility || null,
    isTeamNote: rawNote.is_team_note || rawNote.isTeamNote || rawNote.visibility === 'TEAM',
    attachmentCount: rawNote.attachment_count || rawNote.attachmentCount || rawNote.attachments?.length || 0,
    linkCount: rawNote.link_count || rawNote.linkCount || rawNote.links?.length || 0,
    expiresAt: rawNote.expires_at || rawNote.expiresAt || null,
    raw: rawNote,
  };
};

export const NotesTabProvider = ({ children, contactId }) => {
  // State
  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState(initialMeta);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Refs for request tracking
  const fetchIdRef = useRef(0);

  // Fetch notes from API
  const fetchNotes = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!contactId) return;

      const requestId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const params = {
          party_uuid: contactId,
          page,
          page_size: pageSize,
          order_by: 'last_update',
          order_direction: 'DESC',
        };

        // Add category filter if not "all"
        if (filters.category !== 'all') {
          params.filters = { category: filters.category };
        }

        // Add search if present
        if (filters.search?.trim()) {
          params.search = { keyword: filters.search.trim() };
        }

        const response = await listNoteActivities(params);

        if (requestId === fetchIdRef.current) {
          // Map the response body to our normalized format
          const items = (response.body || []).map(mapNoteItem);
          
          // Apply persisted pin status from localStorage
          const pinnedIds = getPinnedIdsFromStorage(contactId);
          const itemsWithPins = items.map((note) => ({
            ...note,
            isPinned: pinnedIds.includes(note.id),
          }));
          
          // Sort pinned notes first
          const sortedItems = itemsWithPins.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0;
          });
          
          setNotes(sortedItems);

          // Update meta from response
          const responseMeta = response.meta || {};
          setMeta({
            total_items: responseMeta.total_items || responseMeta.totalItems || items.length,
            total_pages: responseMeta.total_pages || responseMeta.totalPages || 1,
            current_page: responseMeta.current_page || responseMeta.currentPage || page,
            per_page: responseMeta.per_page || responseMeta.perPage || pageSize,
            has_next: responseMeta.has_next || responseMeta.hasNext || false,
            has_previous: responseMeta.has_previous || responseMeta.hasPrevious || false,
          });

          // Clear selection on data change
          setSelectedIds(new Set());
          setSelectAll(false);
        }
      } catch (err) {
        if (requestId === fetchIdRef.current) {
          console.error('Error fetching notes:', err);
          setError(err);
        }
      } finally {
        if (requestId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [contactId, filters]
  );

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // Selection handlers
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
    setSelectAll(false);
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(notes.map((item) => item.id));
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  }, [selectAll, notes]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAll(false);
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback(
    (page) => {
      fetchNotes(page, meta.per_page);
    },
    [fetchNotes, meta.per_page]
  );

  const handleItemsPerPageChange = useCallback(
    (pageSize) => {
      fetchNotes(1, pageSize);
    },
    [fetchNotes]
  );

  // CRUD Operations
  const createNoteHandler = useCallback(
    async (noteData) => {
      try {
        const result = await createNote({
          entity_type: 'party',
          entity_uuid: contactId,
          ...noteData,
        });
        // Refresh list from page 1 so new note appears at top
        await fetchNotes(1, meta.per_page);
        return result;
      } catch (err) {
        console.error('Error creating note:', err);
        throw err;
      }
    },
    [contactId, fetchNotes, meta.per_page]
  );

  const getNoteDetailHandler = useCallback(async (noteUuid) => {
    try {
      const result = await getNoteDetail(noteUuid);
      return result.body;
    } catch (err) {
      console.error('Error getting note detail:', err);
      throw err;
    }
  }, []);

  const updateNoteHandler = useCallback(
    async (noteData) => {
      try {
        const result = await updateNote(noteData);
        // Refresh list after updating
        await fetchNotes(meta.current_page, meta.per_page);
        return result;
      } catch (err) {
        console.error('Error updating note:', err);
        throw err;
      }
    },
    [fetchNotes, meta.current_page, meta.per_page]
  );

  const deleteNoteHandler = useCallback(
    async (noteUuid) => {
      try {
        // Optimistic update - remove from list immediately
        setNotes((prev) => {
          const filtered = prev.filter((item) => item.noteUuid !== noteUuid && item.id !== noteUuid);
          
          // Update localStorage to remove deleted note from pins
          const pinnedIds = filtered.filter((n) => n.isPinned).map((n) => n.id);
          savePinnedIdsToStorage(contactId, pinnedIds);
          
          return filtered;
        });
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(noteUuid);
          return newSet;
        });

        await deleteNote(noteUuid);
        
        // Refresh to get accurate count
        await fetchNotes(meta.current_page, meta.per_page);
      } catch (err) {
        console.error('Error deleting note:', err);
        // Refresh to restore state on error
        await fetchNotes(meta.current_page, meta.per_page);
        throw err;
      }
    },
    [contactId, fetchNotes, meta.current_page, meta.per_page]
  );

  // Pin/Unpin Note Handler
  const pinNoteHandler = useCallback(
    (noteId, isPinned) => {
      // Optimistic update - toggle pin and sort
      setNotes((prev) => {
        const updated = prev.map((note) =>
          note.id === noteId ? { ...note, isPinned } : note
        );
        
        // Save pinned IDs to localStorage
        const pinnedIds = updated.filter((n) => n.isPinned).map((n) => n.id);
        savePinnedIdsToStorage(contactId, pinnedIds);
        
        // Sort: pinned notes first, then by lastUpdated
        return updated.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return 0; // Keep original order for same pin status
        });
      });
    },
    [contactId]
  );

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.category !== 'all';
  }, [filters]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const value = useMemo(
    () => ({
      // State
      notes,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,

      // Actions
      fetchNotes,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,

      // CRUD
      createNote: createNoteHandler,
      getNoteDetail: getNoteDetailHandler,
      updateNote: updateNoteHandler,
      deleteNote: deleteNoteHandler,
      pinNote: pinNoteHandler,
    }),
    [
      notes,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,
      fetchNotes,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,
      createNoteHandler,
      getNoteDetailHandler,
      updateNoteHandler,
      deleteNoteHandler,
      pinNoteHandler,
    ]
  );

  return (
    <NotesTabContext.Provider value={value}>{children}</NotesTabContext.Provider>
  );
};

export const useNotesTab = () => {
  const context = useContext(NotesTabContext);
  if (!context) {
    throw new Error('useNotesTab must be used within NotesTabProvider');
  }
  return context;
};

export default NotesTabContext;
